import { abs } from "@suse-o11y/abs";
import { sortBy } from "@suse-o11y/sort-by";

import { STATIC_TIME_RANGES, type TimeRange } from "../../../app/state/view/timeline/TimeRange";

import type { NavigationMiddleware } from "./NavigationMiddleware";

const SORTED_TIME_RANGES = sortBy(STATIC_TIME_RANGES, (timeRange) => timeRange.duration());

/**
 * Returns the static time range whose duration is closest to the source range's duration.
 * Ties are resolved in favor of the longer candidate.
 */
function closestStaticRangeByDuration(source: TimeRange): TimeRange {
	const sourceDuration = source.duration();

	return SORTED_TIME_RANGES.reduce((closest, candidate) =>
		abs(candidate.duration() - sourceDuration) <= abs(closest.duration() - sourceDuration) ? candidate : closest,
	);
}

/**
 * Navigation middleware that resolves invalid timeline states by comparing the previous
 * and next navigation payloads to detect transitions.
 *
 * Two invariants are enforced:
 *
 * 1. **Entering time-travel.** When `next` introduces a specific range that was not
 *    present in `prev`, the navigation is treated as entering time-travel mode. If
 *    `next` has no timestamp, it is set to the range's end so the snapshot is anchored.
 *
 * 2. **Going live.** When `next` keeps a specific range but clears the timestamp (i.e.
 *    `prev` was already in time-travel with a specific range), the range is normalized
 *    to the static (rolling) preset whose duration is closest to it. This keeps the
 *    viewing window proportional while making it rolling.
 *
 * In all other cases `next` is returned unchanged:
 * - Specific range with a timestamp present (valid time-travel state).
 * - Dynamic or absent range (downstream selector falls back to the configured default).
 */
export const timeTransitionMiddleware = ((next, prev) => {
	const nextRange = next.search.timeRange;
	const prevRange = prev.search.timeRange;

	if (!prevRange?.isSpecific() && nextRange?.isSpecific()) {
		return {
			pathname: next.pathname,
			search: { ...next.search, timestamp: next.search.timestamp ?? nextRange.end },
		};
	}

	if (next.search.timestamp == null && nextRange?.isSpecific()) {
		return {
			pathname: next.pathname,
			search: { ...next.search, timeRange: closestStaticRangeByDuration(nextRange) },
		};
	}

	return next;
}) satisfies NavigationMiddleware;
